import { h, Fragment } from "preact"
import { MultiSlider } from "./MultiSlider"
import { useRange } from "../hooks/useRange"

/**
 * @group Components
 */
export interface RangeSliderProps {
    /**
     * Facet ID from the response, in most cases it is the same as field name.
     */
    id: string
    /**
     * Callback that will be called when the user releases slider handle.
     */
    onChange?(range: [number, number]): void
    /**
     * Callback that will be called when sliding the slider.
     */
    onSlide?(range: [number, number]): void
    /**
     * Prefix that will be appended to all component classes.
     *
     * @default 'ns-'
     */
    classesPrefix?: string
    /**
     * Component wrapper class.
     *
     * @default 'ns-range-slider'
     */
    className?: string
    /**
     * Callback to format display value.
     */
    valueFormat?: (value: number) => string
}

/**
 * Range slider component that renders range slider based on response stats facet.
 *
 * @example
 * ```tsx
 * return (props) => {
 *  return <RangeSlider id={props.facet.id}/>
 * }
 * ```
 *
 * @group Components
 */
export function RangeSlider(props: RangeSliderProps) {
    const { min, max, range, updateRange } = useRange(props.id)

    return typeof min === "number" && typeof max === "number" && min !== max ? (
        <MultiSlider
            key={`range_slider_${min}_${max}_${range.join("_")}`}
            classesPrefix={props.classesPrefix || "ns-"}
            className={props.className || "range-slider"}
            onChange={([from, to]) => {
                if (props.onSlide) {
                    props.onSlide([Math.floor(from || min), Math.ceil(to || max)])
                }
            }}
            onAfterChange={([from, to]) => {
                updateRange([from, to])
            }}
            defaultValue={[Math.floor(range[0] || min), Math.ceil(range[1] || max)]}
            min={Math.floor(min)}
            max={Math.ceil(max)}
            tipFormatter={props.valueFormat}
        />
    ) : (
        <Fragment />
    )
}
